import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Save as SaveIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { teamApi } from '@/services/api';
import { AIContextTemplate } from '@/types';

interface ContextTemplatesManagerProps {
  teamId: string;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

export default function ContextTemplatesManager({ teamId, onNotify }: ContextTemplatesManagerProps) {
  const queryClient = useQueryClient();
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AIContextTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState<{
    context_type: AIContextTemplate['context_type'];
    description: string;
    messages: Array<{ role: string; content: string }>;
    is_active: boolean;
  }>({
    context_type: 'freeform',
    description: '',
    messages: [{ role: 'system', content: '' }],
    is_active: true,
  });

  const { data: contextTemplates } = useQuery({
    queryKey: ['team-context-templates', teamId],
    queryFn: () => teamApi.getContextTemplates(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  const createTemplateMutation = useMutation({
    mutationFn: (data: Partial<AIContextTemplate>) => teamApi.addContextTemplate(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-context-templates', teamId] });
      onNotify('Context template created successfully!', 'success');
      handleCloseDialog();
    },
    onError: (error: Error) => {
      onNotify(`Failed to create: ${getErrorMessage(error)}`, 'error');
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AIContextTemplate> }) =>
      teamApi.updateContextTemplate(teamId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-context-templates', teamId] });
      onNotify('Context template updated successfully!', 'success');
      handleCloseDialog();
    },
    onError: (error: Error) => {
      onNotify(`Failed to update: ${getErrorMessage(error)}`, 'error');
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (id: string) => teamApi.removeContextTemplate(teamId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-context-templates', teamId] });
      onNotify('Context template deleted successfully!', 'success');
    },
    onError: (error: Error) => {
      onNotify(`Failed to delete: ${getErrorMessage(error)}`, 'error');
    },
  });

  const handleOpenDialog = (template?: AIContextTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        context_type: template.context_type,
        description: template.description || '',
        messages: template.messages || [{ role: 'system', content: '' }],
        is_active: template.is_active,
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        context_type: 'freeform',
        description: '',
        messages: [{ role: 'system', content: '' }],
        is_active: true,
      });
    }
    setTemplateDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setTemplateDialogOpen(false);
    setEditingTemplate(null);
    setTemplateForm({
      context_type: 'freeform',
      description: '',
      messages: [{ role: 'system', content: '' }],
      is_active: true,
    });
  };

  const handleSave = () => {
    const data = {
      context_type: templateForm.context_type,
      description: templateForm.description || undefined,
      messages: templateForm.messages.filter(m => m.content.trim() !== ''),
      is_active: templateForm.is_active,
    };

    if (editingTemplate) {
      updateTemplateMutation.mutate({ id: editingTemplate.id, data });
    } else {
      createTemplateMutation.mutate(data);
    }
  };

  const handleAddMessage = () => {
    setTemplateForm(prev => ({
      ...prev,
      messages: [...prev.messages, { role: 'user', content: '' }],
    }));
  };

  const handleRemoveMessage = (index: number) => {
    setTemplateForm(prev => ({
      ...prev,
      messages: prev.messages.filter((_, i) => i !== index),
    }));
  };

  const handleMessageChange = (index: number, field: 'role' | 'content', value: string) => {
    setTemplateForm(prev => ({
      ...prev,
      messages: prev.messages.map((msg, i) => (i === index ? { ...msg, [field]: value } : msg)),
    }));
  };

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Create context templates with example conversations to train the AI how to respond to different query types.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Template
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Context Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Messages</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Version</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contextTemplates?.map((template) => (
              <TableRow key={template.id}>
                <TableCell>
                  <Chip
                    label={template.context_type}
                    size="small"
                    color={
                      template.context_type === 'general' ? 'primary' :
                      template.context_type === 'github' ? 'secondary' :
                      template.context_type === 'jira' ? 'info' :
                      template.context_type === 'jira_incidents' ? 'warning' :
                      'default'
                    }
                  />
                </TableCell>
                <TableCell>{template.description || '-'}</TableCell>
                <TableCell>{template.messages?.length || 0} messages</TableCell>
                <TableCell>
                  <Chip
                    label={template.is_active ? 'Active' : 'Inactive'}
                    color={template.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>v{template.version}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(template)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => deleteTemplateMutation.mutate(template.id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {contextTemplates?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No AI context templates configured yet. Add templates with example conversations to train the AI.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Template Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTemplate ? 'Edit AI Context Template' : 'Create AI Context Template'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add example conversations to train the AI how to respond. The AI will use these examples
            as reference when generating responses for this context type.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Context Type</InputLabel>
                <Select
                  value={templateForm.context_type}
                  label="Context Type"
                  onChange={(e) => setTemplateForm(prev => ({
                    ...prev,
                    context_type: e.target.value as AIContextTemplate['context_type']
                  }))}
                >
                  <MenuItem value="general">General - Default queries</MenuItem>
                  <MenuItem value="github">GitHub - Repository/PR queries</MenuItem>
                  <MenuItem value="jira">JIRA - Ticket queries</MenuItem>
                  <MenuItem value="jira_incidents">JIRA Incidents - Incident handling</MenuItem>
                  <MenuItem value="freeform">Freeform - Custom context</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={templateForm.is_active ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) => setTemplateForm(prev => ({
                    ...prev,
                    is_active: e.target.value === 'active'
                  }))}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={templateForm.description}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe when this template should be used..."
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Example Messages
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={handleAddMessage}>
              Add Message
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a conversation flow showing how the AI should respond. Use "system" for instructions,
            "user" for example questions, and "assistant" for ideal AI responses.
          </Typography>

          {templateForm.messages.map((msg, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={msg.role}
                    label="Role"
                    onChange={(e) => handleMessageChange(index, 'role', e.target.value)}
                  >
                    <MenuItem value="system">System</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="assistant">Assistant</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ flexGrow: 1 }} />
                {templateForm.messages.length > 1 && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRemoveMessage(index)}
                  >
                    Remove
                  </Button>
                )}
              </Box>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={msg.content}
                onChange={(e) => handleMessageChange(index, 'content', e.target.value)}
                placeholder={
                  msg.role === 'system' ? 'System instructions or context...' :
                  msg.role === 'user' ? 'Example user question...' :
                  'Example AI response...'
                }
              />
            </Paper>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createTemplateMutation.isPending || updateTemplateMutation.isPending}
            startIcon={<SaveIcon />}
          >
            {createTemplateMutation.isPending || updateTemplateMutation.isPending ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
